import { useState, useRef } from 'react';
import { X, Download, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useImportCsv } from '@/hooks/useCampaignCatalog';

interface Props {
  campaignId: string;
  onClose: () => void;
}

const CSV_TEMPLATE = `name,price,buy_url,sku,delivery_days_min,delivery_days_max
Pneu Michelin 205/55 R16,459.90,https://loja.com/pneu-michelin,MICH-205,3,7
Pastilha de freio Bosch,189.90,https://loja.com/pastilha-bosch,BOSCH-PF1,1,3`;

export function CsvImportModal({ campaignId, onClose }: Props) {
  const importMutation = useImportCsv(campaignId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  function handleDownloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template-produtos.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleImport() {
    if (!file) return;
    importMutation.mutate(file);
  }

  const result = importMutation.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Importar produtos via CSV</h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          {/* Step 1: Download template */}
          <div>
            <p className="text-sm text-muted-foreground">
              1. Baixe o template CSV com as colunas corretas
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
            >
              <Download size={14} />
              Baixar template
            </button>
          </div>

          {/* Step 2: Upload */}
          <div>
            <p className="text-sm text-muted-foreground">
              2. Preencha e faça upload do arquivo
            </p>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border py-8 hover:bg-muted"
            >
              {file ? (
                <span className="text-sm font-medium">{file.name}</span>
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload size={20} />
                  <span className="text-xs">Clique para selecionar o CSV</span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-2 rounded-lg bg-muted p-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                <span className="font-medium">{result.imported} produtos importados</span>
              </div>
              {result.errors.length > 0 && (
                <div className="space-y-1">
                  {result.errors.map((err) => (
                    <div key={err.line} className="flex items-center gap-2 text-xs text-destructive">
                      <AlertCircle size={12} />
                      Linha {err.line}: {err.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              {result ? 'Fechar' : 'Cancelar'}
            </button>
            {!result && (
              <button
                onClick={handleImport}
                disabled={!file || importMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {importMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Upload size={14} />
                )}
                Importar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
